import React, { useEffect, useState } from "react";
import { Network, Wallet, TrendingUp, Users, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (n, d = 2) => (n === null || n === undefined ? "0.00" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));

export default function CommissionTree() {
  const [data, setData] = useState(null);
  const load = () => api.get("/trading/commission/tree").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 10000); return () => clearInterval(id); }, []);

  const claim = async () => {
    const v = window.prompt("Move trading commission to your withdrawable balance (min $10):", String(data?.wallet_balance || 0));
    if (v === null || v === "") return;
    try { await api.post("/trading/commission/withdraw", { amount: Number(v) }); toast.success("Moved to withdrawable balance"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="commission-tree-page">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-extrabold text-white"><Network className="text-emerald-400" /> Self Trade IB</h1>
        <p className="mt-1 text-sm text-emerald-200/60">Earnings from your 10-level team's self-trading.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {[["Claimable Balance", data?.wallet_balance, Wallet, "text-amber-300"], ["Total Earned", data?.total_earned, TrendingUp, "text-emerald-400"], ["Active Levels", (data?.levels || []).filter((l) => l.earned > 0).length, Users, "text-sky-300"]].map(([l, v, Ic, c], i) => (
          <div key={l} className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`comm-stat-${i}`}>
            <p className="flex items-center gap-1 text-[11px] uppercase text-emerald-200/50"><Ic size={13} /> {l}</p>
            <p className={`mt-1 font-mono text-2xl font-extrabold ${c}`}>{i === 2 ? v || 0 : `$${fmt(v)}`}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between rounded-xl border border-amber-400/25 bg-amber-400/5 px-4 py-3">
        <p className="flex items-center gap-2 text-[12px] text-amber-200/90"><Info size={15} className="shrink-0 text-amber-400" /> When your team self-trades and withdraws profit, you earn L1 = 10% and L2–L10 = 2% each (28% total). Uplines must be active with min $30.</p>
        <button onClick={claim} data-testid="claim-commission-btn" className="shrink-0 rounded-lg bg-amber-400 px-4 py-2 text-xs font-extrabold text-black hover:bg-amber-300">Claim to Withdrawable</button>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <p className="mb-2 text-sm font-bold text-white">Level-wise Breakup</p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12px]">
            <thead className="text-emerald-200/50"><tr><th className="py-1">Level</th><th>Rate</th><th>Team Members</th><th>Payouts</th><th className="text-right">Earned</th></tr></thead>
            <tbody>
              {(data?.levels || []).map((l) => (
                <tr key={l.level} className="border-t border-white/5" data-testid={`comm-level-${l.level}`}>
                  <td className="py-2 font-extrabold" style={{ color: l.level === 1 ? "#f5c542" : "#8fdcc0" }}>L{l.level}</td>
                  <td className="text-white">{l.pct}%</td>
                  <td className="text-white">{l.members}</td>
                  <td className="text-white">{l.txns}</td>
                  <td className={`text-right font-bold ${l.earned > 0 ? "text-emerald-400" : "text-white/40"}`}>${fmt(l.earned)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
        <p className="mb-2 text-sm font-bold text-white">Recent Commission</p>
        {(!data || data.recent.length === 0) ? (
          <p className="py-4 text-center text-sm text-emerald-200/40">No team commission yet. Share your referral link and grow your self-trading team!</p>
        ) : (
          <div className="space-y-1">
            {data.recent.map((r, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2 text-[12px]" data-testid={`comm-recent-${i}`}>
                <span className="text-white">L{r.level} · from <b>{r.from_member}</b></span>
                <span className="text-emerald-400 font-bold">+${fmt(r.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
